'use client'

import { Link, Locales } from '@/navigation'
import { css, cx } from '@/styled-system/css'
import { splitCssProps } from '@/styled-system/jsx'
import { button, type ButtonVariantProps } from '@/styled-system/recipes'
import type { Assign, JsxStyleProps } from '@/styled-system/types'
import {
  ForwardedRef,
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react'

export interface ButtonProps
  extends Assign<JsxStyleProps, ButtonHTMLAttributes<HTMLButtonElement>>,
    ButtonVariantProps {
  children?: ReactNode
  href?: string
  locale?: Locales
}

const Button = forwardRef<HTMLElement, ButtonProps>((props, ref) => {
  const [variantProps, checkboxProps] = button.splitVariantProps(props)
  const [cssProps, localProps] = splitCssProps(checkboxProps)
  const { children, className, href, locale, disabled, ...rest } = localProps
  const styles = button(variantProps)

  const content = (
    <>
      {children}

      <span className={styles.loadingIndicator}>
        {/* CSS Spinner */}
        <div
          className={css({
            display: 'inline-block',
            width: '1em', // Adjust size as needed via props or theme
            height: '1em',
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: 'currentColor',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite', // Assumes @keyframes spin is defined globally
          })}
        />
      </span>
    </>
  )

  if (href) {
    return href.startsWith('https://') ? (
      <a
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        className={cx(styles.root, css(cssProps), className)}
        {...(rest as any)}
      >
        {content}
      </a>
    ) : (
      <Link
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        href={href}
        locale={locale}
        className={cx(styles.root, css(cssProps), className)}
        {...(rest as any)}
      >
        {content}
      </Link>
    )
  }

  return (
    <button
      ref={ref as ForwardedRef<HTMLButtonElement>}
      className={cx(styles.root, css(cssProps), className)}
      disabled={disabled}
      {...rest}
    >
      {content}
    </button>
  )
})

Button.displayName = 'Button'

export default Button
